import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, ProgressBar, Badge, OverlayTrigger, Tooltip as BSTooltip } from 'react-bootstrap';
import confetti from 'canvas-confetti';
import { colors } from '../brand-colors';
import SpeechInput from '../components/SpeechInput';

// Brand-styled button styles
const brandButtonStyle = {
  background: `linear-gradient(180deg, ${colors.redDark} 0%, ${colors.greyDarkest} 100%)`,
  border: 'none',
  color: colors.greyLightest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
  transition: 'all 0.2s ease',
};

const brandButtonHoverStyle = {
  background: `linear-gradient(180deg, ${colors.red} 0%, ${colors.greyDarker} 100%)`,
};

const successButtonStyle = {
  background: `linear-gradient(180deg, ${colors.goldMid} 0%, ${colors.goldDark} 100%)`,
  border: 'none',
  color: colors.greyDarkest,
  fontWeight: '600',
  padding: '0.75rem 2rem',
  fontSize: '1.1rem',
  transition: 'all 0.2s ease',
};

const successButtonHoverStyle = {
  background: `linear-gradient(180deg, ${colors.goldLight} 0%, ${colors.gold} 100%)`,
};

// Glossary of terms for business owners
const GLOSSARY = {
  'intellectual property': 'Your unique expertise, frameworks, and methods that set you apart from competitors - the "secret sauce" of how you do what you do.',
  'IP': 'Short for Intellectual Property - your unique expertise and methods.',
  'IP framework': 'A structured, documented version of your expertise that can be taught, licensed, or referenced.',
  'codified': 'Written down and organized in a clear, teachable format.',
  'schema': 'Structured data markup that helps search engines and AI understand your content - like adding labels to your expertise so AI can find and cite it.',
  'knowledge graph': 'The database AI platforms use to understand who experts are and what they know. Getting into the knowledge graph means AI will mention you.',
  'AI citation': 'When AI platforms like ChatGPT or Claude mention you or your work in their answers.',
  'methodology': 'Your systematic approach or process for solving a specific problem.',
  'positioning': 'How you\'re perceived in your market - what makes you the obvious choice for your ideal clients.',
};

// Tooltip component for glossary terms
const Term = ({ term, children }) => {
  const definition = GLOSSARY[term.toLowerCase()] || GLOSSARY[term];
  if (!definition) return children || term;

  return (
    <OverlayTrigger
      placement="top"
      overlay={<BSTooltip style={{ maxWidth: '300px' }}>{definition}</BSTooltip>}
    >
      <span style={{
        borderBottom: '1px dotted #666',
        cursor: 'help',
        fontWeight: 'inherit'
      }}>
        {children || term}
      </span>
    </OverlayTrigger>
  );
};

/**
 * MindToMethod - IP Extraction Lead Magnet
 * No authentication required. Conversational AI interface.
 *
 * Phases:
 * 1. Capture (name, email, company, industry)
 * 2. Phase 0: Wake-up call (AI search results)
 * 3. Sections 1-5: Guided extraction
 * 4. Output: Generated IP framework with PDF
 */
function MindToMethod() {
  // State management
  const [phase, setPhase] = useState('capture'); // capture, wakeup, extraction, naming, output
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingSession, setExistingSession] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: ''
  });

  // Wake-up call data
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Extraction state
  const [currentSection, setCurrentSection] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionKey, setQuestionKey] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [progress, setProgress] = useState(0);
  const [samples, setSamples] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const answerBeforeSpeechRef = useRef(''); // Track text before speech started (ref avoids closure issues)
  const [coachingResponse, setCoachingResponse] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(4);
  const [conversationHistory, setConversationHistory] = useState([]);

  // Naming state
  const [namingOptions, setNamingOptions] = useState(null);
  const [synopsis, setSynopsis] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [customName, setCustomName] = useState('');
  const [namingChoice, setNamingChoice] = useState(''); // 'have_name' or 'need_help'
  const [proposedName, setProposedName] = useState('');

  // Output state
  const [output, setOutput] = useState(null);
  const [pdfSending, setPdfSending] = useState(false);
  const [pdfSent, setPdfSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [updatedEmail, setUpdatedEmail] = useState('');

  const answerInputRef = useRef(null);
  const conversationEndRef = useRef(null);

  // Auto-scroll conversation to bottom when new messages arrive
  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Check if email already exists
  const checkEmail = async () => {
    if (!formData.email) return;

    try {
      const response = await fetch('/api/mind-to-method/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.toLowerCase() })
      });
      const data = await response.json();

      if (data.exists) {
        setExistingSession(data);
      } else {
        setExistingSession(null);
      }
    } catch (err) {
      // Ignore check errors
    }
  };

  // Start new session
  const handleStart = async () => {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!formData.email.includes('@') || !formData.email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/mind-to-method/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          company: formData.company.trim(),
          industry: formData.industry.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setSessionId(data.session_id);

        if (data.is_existing) {
          // Existing session - restore state
          await loadExistingSession(data.session_id, data.status, data.output_generated);
        } else {
          // New session - proceed to Phase 0
          setPhase('wakeup');
        }
      } else {
        setError(data.error || 'Failed to start session');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // Load existing session
  const loadExistingSession = async (id, status, outputGenerated) => {
    try {
      const response = await fetch(`/api/mind-to-method/get-session/${id}`);
      const data = await response.json();

      if (data.success) {
        const session = data.session;

        if (outputGenerated) {
          // Go straight to output
          setPhase('output');
          generateOutput(id);
        } else if (status === 'completed') {
          // Ready for output
          setPhase('output');
          generateOutput(id);
        } else if (session.ai_search_results) {
          // Has phase 0 results, continue extraction
          setSearchResults(session.ai_search_results);
          if (session.current_section && session.current_question) {
            await continueFromQuestion(id);
          } else {
            setPhase('wakeup');
          }
        } else {
          setPhase('wakeup');
        }
      }
    } catch (err) {
      setPhase('wakeup');
    }
  };

  // Run Phase 0 searches
  const runPhase0 = async () => {
    setSearchLoading(true);

    try {
      const response = await fetch(`/api/mind-to-method/phase0/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setSearchResults(data.results);
      } else {
        setError('Failed to run AI search. Continuing anyway...');
        setSearchResults({ error: true });
      }
    } catch (err) {
      setError('Search failed. Continuing anyway...');
      setSearchResults({ error: true });
    }

    setSearchLoading(false);
  };

  // Start extraction
  const startExtraction = async () => {
    setLoading(true);

    try {
      const response = await fetch(`/api/mind-to-method/start-extraction/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setCurrentSection(data.section);
        setSectionTitle(data.section_title);
        setProgress(data.progress);
        setCurrentQuestion(data.question);
        setQuestionKey(data.question_key);
        setSamples(data.samples || []);
        setPhase('extraction');
        setCoachingResponse('');
        setUserAnswer('');
        setConversationHistory([{ role: 'assistant', content: data.question }]);
      } else {
        setError(data.error || 'Failed to start extraction');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // Continue from a specific question
  const continueFromQuestion = async (id) => {
    try {
      const response = await fetch(`/api/mind-to-method/resume-extraction/${id}`);
      const data = await response.json();

      if (data.success) {
        setCurrentSection(data.section);
        setSectionTitle(data.section_title);
        setProgress(data.progress);
        setCurrentQuestion(data.question);
        setQuestionKey(data.question_key);
        setSamples(data.samples || []);
        setCoachingResponse('');
        setUserAnswer('');
        // Initialize conversation with the current question
        setConversationHistory([{ role: 'assistant', content: data.question }]);
        setPhase('extraction');
      } else {
        // Fall back to starting fresh
        startExtraction();
      }
    } catch (err) {
      // Fall back to starting fresh
      startExtraction();
    }
  };

  // Submit answer - accepts optional override for async state situations
  const handleSubmitAnswer = async (answerOverride = null) => {
    const answerToSubmit = answerOverride || userAnswer.trim();

    if (!answerToSubmit) {
      setError('Please provide an answer');
      return;
    }

    if (!questionKey) {
      setError('Session error - please refresh the page');
      return;
    }

    setLoading(true);
    setError('');

    // Store current question key for verification
    const submittedQuestionKey = questionKey;
    console.log('[MindToMethod] Submitting answer for question:', submittedQuestionKey, 'Answer:', answerToSubmit);

    try {
      const response = await fetch(`/api/mind-to-method/answer/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answer: answerToSubmit,
          question_key: submittedQuestionKey
        })
      });

      const data = await response.json();

      if (data.success) {
        // Log for debugging
        if (data.answered_question && data.answered_question !== submittedQuestionKey) {
          console.warn('[MindToMethod] Question key was corrected by server:', {
            sent: submittedQuestionKey,
            processed: data.answered_question
          });
        }

        // Add answer to history with question context
        const newHistory = [...conversationHistory, {
          role: 'user',
          content: userAnswer,
          questionKey: data.answered_question || submittedQuestionKey
        }];

        if (data.needs_coaching) {
          // Got coaching response
          setCoachingResponse(data.coaching_response);
          setAttemptsRemaining(data.attempts_remaining);
          newHistory.push({ role: 'assistant', content: data.coaching_response });
          setConversationHistory(newHistory);
          setUserAnswer('');
          // Ensure questionKey stays in sync
          if (data.question_key) {
            setQuestionKey(data.question_key);
          }
        } else if (data.completed) {
          // All questions done - check if they already provided a name
          setConversationHistory(newHistory);
          if (data.has_framework_name && data.framework_name) {
            // User already provided a name - skip naming page, go straight to output
            console.log('[MindToMethod] User provided name:', data.framework_name, '- skipping to output');
            setPhase('output');
            await generateOutput();
          } else {
            // Need to brainstorm a name
            setPhase('naming');
            await loadNamingOptions();
          }
        } else {
          // Move to next question
          console.log('[MindToMethod] Moving to next question:', data.question_key);
          setCurrentSection(data.section);
          setSectionTitle(data.section_title);
          setProgress(data.progress);
          setCurrentQuestion(data.question);
          setQuestionKey(data.question_key);
          setSamples(data.samples || []);
          setCoachingResponse('');
          setUserAnswer('');
          setAttemptsRemaining(4);
          setNamingChoice(''); // Reset for s5_naming
          setProposedName(''); // Reset for s5_naming
          newHistory.push({ role: 'assistant', content: data.question });
          setConversationHistory(newHistory);
        }
      } else {
        setError(data.error || 'Failed to submit answer');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
    setTimeout(() => answerInputRef.current?.focus(), 100);
  };

  // Load naming options
  const loadNamingOptions = async () => {
    try {
      const response = await fetch(`/api/mind-to-method/generate-names/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setNamingOptions(data.naming_options);
        if (data.synopsis) {
          setSynopsis(data.synopsis);
        }
      }
    } catch (err) {
      // Use defaults
      setNamingOptions({
        descriptive: ['The Framework Method'],
        action_oriented: ['Impact System'],
        metaphor_based: ['The Blueprint']
      });
    }
  };

  // Set framework name
  const handleSetName = async () => {
    const name = selectedName === 'custom' ? customName.trim() : selectedName;

    if (!name) {
      setError('Please select or enter a name');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`/api/mind-to-method/set-framework-name/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });

      const data = await response.json();

      if (data.success) {
        setPhase('output');
        await generateOutput(sessionId);
      } else {
        setError(data.error || 'Failed to save name');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // Generate output
  const generateOutput = async (id = sessionId) => {
    setLoading(true);

    try {
      const response = await fetch(`/api/mind-to-method/generate-output/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setOutput(data.output);
      } else {
        setError(data.error || 'Failed to generate output');
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }

    setLoading(false);
  };

  // Send PDF via email
  const handleSendPdf = async (useUpdatedEmail = false) => {
    setPdfSending(true);
    setEmailError('');

    const emailToUse = useUpdatedEmail && updatedEmail ? updatedEmail.trim().toLowerCase() : null;

    try {
      const response = await fetch(`/api/mind-to-method/send-pdf/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailToUse ? { email: emailToUse } : {})
      });

      const data = await response.json();

      if (data.success) {
        setPdfSent(true);
        setEmailError('');
        if (emailToUse) {
          setFormData(prev => ({ ...prev, email: emailToUse }));
        }
      } else {
        setEmailError(data.error || 'Failed to send PDF. Please check your email address.');
        setUpdatedEmail(formData.email); // Pre-fill with current email for editing
      }
    } catch (err) {
      setEmailError('Failed to send PDF. Please check your email address and try again.');
      setUpdatedEmail(formData.email);
    }

    setPdfSending(false);
  };

  // Trigger Phase 0 when entering wakeup phase
  useEffect(() => {
    if (phase === 'wakeup' && sessionId && !searchResults) {
      runPhase0();
    }
  }, [phase, sessionId]);

  // Celebrate with confetti when output is ready
  useEffect(() => {
    if (phase === 'output' && output && !loading) {
      // Fire confetti from both sides - brand colors (red + gold)
      const end = Date.now() + 1500;
      const colors = ['#a10000', '#ba1d1d', '#db3838', '#ddb75e', '#cca557', '#ba934f'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
    }
  }, [phase, output, loading]);

  // ============================================
  // RENDER: Capture Phase
  // ============================================
  if (phase === 'capture') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h1 className="text-center mb-2" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1a365d' }}>
                  Mind to Method
                </h1>
                <p className="text-center text-muted mb-4">
                  Discover and <Term term="codified">codify</Term> your unique <Term term="intellectual property">intellectual property</Term> in 15 minutes
                </p>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                {existingSession && (
                  <Alert variant="info">
                    We found an existing session for this email.
                    {existingSession.output_generated
                      ? " Click below to view your results."
                      : " Click below to continue where you left off."}
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label><strong>Your Name</strong> <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Sarah Johnson"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Email Address</strong> <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={checkEmail}
                  />
                  <Form.Text className="text-muted">We'll send your <Term term="IP framework">IP framework</Term> here. One free framework per user.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Company Name</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Acme Consulting"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label><strong>Industry/Expertise Area</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., Marketing Strategy, Leadership Coaching"
                    value={formData.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                  />
                </Form.Group>

                <Button
                  size="lg"
                  className="w-100"
                  onClick={handleStart}
                  disabled={loading}
                  style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" />Starting...</>
                  ) : existingSession ? (
                    existingSession.output_generated ? 'View My Results' : 'Continue My Session'
                  ) : (
                    'Start IP Extraction'
                  )}
                </Button>

                <p className="text-center text-muted mt-4" style={{ fontSize: '0.85rem' }}>
                  Takes about 15 minutes. Your progress is saved automatically.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Wake-up Call Phase
  // ============================================
  if (phase === 'wakeup') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="text-center mb-4" style={{ color: '#1a365d' }}>
                  The Wake-Up Call
                </h2>

                {/* Why Documented IP Matters - show while loading */}
                <div className="mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px', borderLeft: '4px solid #2f855a' }}>
                  <h5 className="mb-3" style={{ color: '#276749' }}>Why Documented <Term term="IP">IP</Term> Changes Everything</h5>
                  <p className="text-muted mb-3">
                    When your <Term term="intellectual property">intellectual property</Term> is clearly documented and published, something powerful happens:
                  </p>
                  <ul className="mb-0" style={{ color: '#4a5568' }}>
                    <li><strong>AI platforms start <Term term="AI citation">citing you</Term></strong> — your expertise shows up in ChatGPT, Claude, and Perplexity answers</li>
                    <li><strong>Trust compounds</strong> — prospects see your <Term term="methodology">methodology</Term> before they ever contact you</li>
                    <li><strong>Premium <Term term="positioning">positioning</Term></strong> — you're no longer competing on price, you're the recognized expert</li>
                    <li><strong>Business valuation increases</strong> — documented IP is one of the first things buyers and investors look for</li>
                  </ul>
                </div>

                {searchLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p className="text-muted">
                      Searching AI platforms to see what they know about you...
                    </p>
                    <p className="text-muted small">This takes about 30 seconds</p>
                  </div>
                ) : searchResults && !searchResults.error ? (
                  <>
                    <p className="text-center text-muted mb-4">
                      Here's what AI platforms currently know about your expertise:
                    </p>

                    <Card className="mb-3" style={{ backgroundColor: '#f8fafc' }}>
                      <Card.Body>
                        <h6 className="text-muted mb-2">Search: "Who is {formData.name}?"</h6>
                        <p className="mb-0">{searchResults.name_search}</p>
                      </Card.Body>
                    </Card>

                    {searchResults.company_search && searchResults.company_search !== "No company provided" && (
                      <Card className="mb-3" style={{ backgroundColor: '#f8fafc' }}>
                        <Card.Body>
                          <h6 className="text-muted mb-2">Search: "What does your company do?"</h6>
                          <p className="mb-0">{searchResults.company_search}</p>
                        </Card.Body>
                      </Card>
                    )}

                    {searchResults.experts_search && searchResults.experts_search !== "No industry provided" && (
                      <Card className="mb-3" style={{ backgroundColor: '#f8fafc' }}>
                        <Card.Body>
                          <h6 className="text-muted mb-2">Search: "Who are the leading experts in your industry?"</h6>
                          <p className="mb-0">{searchResults.experts_search}</p>
                        </Card.Body>
                      </Card>
                    )}

                    <div className="mt-4 p-3" style={{ backgroundColor: '#fff5f5', borderRadius: '8px' }}>
                      <h5 className="mb-3">Analysis:</h5>
                      <p className="mb-2">
                        {searchResults.analysis?.ai_found_you
                          ? '✓ AI found information about you'
                          : '✗ AI has never heard of you'}
                      </p>
                      <p className="mb-2">
                        {searchResults.analysis?.listed_among_experts
                          ? '✓ You\'re listed among experts'
                          : '✗ You\'re not listed among experts in your field'}
                      </p>
                    </div>

                    <div className="mt-4 p-4 text-center" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
                      <p className="mb-3">
                        <strong>This is what AI knows about you RIGHT NOW.</strong>
                      </p>
                      <p className="mb-4">
                        In the next 15 minutes, we're going to extract ONE piece of intellectual property
                        that positions you as an expert AI platforms can't ignore.
                      </p>
                      <Button
                        size="lg"
                        onClick={() => setPhase('overview')}
                        style={successButtonStyle}
                        onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                        onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                      >
                        Let's Change That
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted mb-4">
                      We couldn't complete the AI search, but don't worry - you can still extract your IP.
                    </p>
                    <Button
                      size="lg"
                      onClick={() => setPhase('overview')}
                      style={successButtonStyle}
                      onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                      onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                    >
                      Continue to IP Extraction
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Overview Phase - Show the 5-layer structure
  // ============================================
  if (phase === 'overview') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="text-center mb-4" style={{ color: '#1a365d' }}>
                  How This Works
                </h2>

                <p className="text-center text-muted mb-4">
                  We'll extract your <Term term="intellectual property">intellectual property</Term> in 5 layers. Each builds on the last
                  to create your complete, documented <Term term="IP framework">framework</Term>.
                </p>

                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                         style={{ width: '32px', height: '32px', minWidth: '32px' }}>1</div>
                    <div>
                      <strong>IDENTITY</strong>
                      <p className="text-muted mb-0 small">Who you help and what patterns you see that others miss</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-3">
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                         style={{ width: '32px', height: '32px', minWidth: '32px' }}>2</div>
                    <div>
                      <strong>PAIN PATTERNS</strong>
                      <p className="text-muted mb-0 small">The mistakes you prevent and failed advice you counter</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-3">
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                         style={{ width: '32px', height: '32px', minWidth: '32px' }}>3</div>
                    <div>
                      <strong>DECISION LOGIC</strong>
                      <p className="text-muted mb-0 small">How you make key choices and the sequence that matters</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-3">
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                         style={{ width: '32px', height: '32px', minWidth: '32px' }}>4</div>
                    <div>
                      <strong>BOUNDARIES</strong>
                      <p className="text-muted mb-0 small">What you deliberately reject and who you're NOT for</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-3">
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                         style={{ width: '32px', height: '32px', minWidth: '32px' }}>5</div>
                    <div>
                      <strong>LANGUAGE</strong>
                      <p className="text-muted mb-0 small">Naming your <Term term="methodology">methodology</Term> so it's memorable and ownable</p>
                    </div>
                  </div>
                </div>

                <div className="p-3 mb-4" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                  <p className="mb-0 text-center">
                    <strong>Your answers become your documented <Term term="IP framework">framework</Term>.</strong><br />
                    <span className="text-muted">Be specific - concrete examples make for stronger <Term term="IP">IP</Term>.</span>
                  </p>
                </div>

                <Button
                  size="lg"
                  className="w-100"
                  onClick={startExtraction}
                  disabled={loading}
                  style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" />Starting...</>
                  ) : (
                    'Begin Extraction'
                  )}
                </Button>

                <p className="text-center text-muted mt-3 small">
                  Takes about 15-20 minutes. Your progress is saved automatically.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Extraction Phase
  // ============================================
  if (phase === 'extraction') {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                {/* Progress */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Badge style={{ backgroundColor: colors.redDark }}>Section {currentSection} of 5</Badge>
                    <span className="text-muted small">{progress}% complete</span>
                  </div>
                  <ProgressBar now={progress} style={{ height: '8px', backgroundColor: colors.greyLight }} className="brand-progress" />
                  <p className="mt-2 mb-0" style={{ color: colors.goldDark, fontWeight: '500' }}>
                    {sectionTitle}
                  </p>
                </div>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                {/* Conversation History */}
                <div
                  className="mb-4 p-3"
                  style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {conversationHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`mb-3 p-3 rounded ${
                        msg.role === 'assistant'
                          ? 'bg-white border'
                          : 'bg-primary text-white ms-4'
                      }`}
                      style={{
                        maxWidth: msg.role === 'user' ? '85%' : '100%',
                        marginLeft: msg.role === 'user' ? 'auto' : '0'
                      }}
                    >
                      {msg.role === 'assistant' && (
                        <small className="text-muted d-block mb-1">AI Coach</small>
                      )}
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    </div>
                  ))}
                  <div ref={conversationEndRef} />
                </div>

                {/* Coaching Alert */}
                {coachingResponse && (
                  <Alert variant="warning" className="mb-3">
                    <strong>Let's dig deeper:</strong> {attemptsRemaining} attempts remaining
                  </Alert>
                )}

                {/* Sample Answers */}
                {samples.length > 0 && !coachingResponse && (
                  <div className="mb-3 p-3" style={{ backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                    <p className="mb-2 text-muted small"><strong>Example answers to guide you:</strong></p>
                    {samples.map((sample, idx) => (
                      <p key={idx} className="mb-1 small text-muted" style={{ fontStyle: 'italic' }}>
                        "{sample}"
                      </p>
                    ))}
                  </div>
                )}

                {/* Answer Input - Special handling for s5_naming */}
                {questionKey === 's5_naming' ? (
                  <div className="mb-3">
                    <div
                      className="p-3 mb-2 rounded"
                      style={{
                        border: namingChoice === 'have_name' ? '2px solid #0d6efd' : '2px solid #dee2e6',
                        backgroundColor: namingChoice === 'have_name' ? '#e7f1ff' : '#fff',
                        cursor: 'pointer'
                      }}
                      onClick={() => { setNamingChoice('have_name'); setUserAnswer(''); }}
                    >
                      <Form.Check
                        type="radio"
                        id="naming-have"
                        name="namingChoice"
                        label={<span style={{ fontSize: '1.1rem', fontWeight: '500' }}>Yes, I have a name for my methodology</span>}
                        checked={namingChoice === 'have_name'}
                        onChange={() => { setNamingChoice('have_name'); setUserAnswer(''); }}
                      />
                    </div>
                    {namingChoice === 'have_name' && (
                      <Form.Control
                        type="text"
                        placeholder="Enter your framework/methodology name..."
                        value={proposedName}
                        onChange={(e) => setProposedName(e.target.value)}
                        className="ms-4 mb-3"
                        style={{ maxWidth: '400px' }}
                        autoFocus
                      />
                    )}
                    <div
                      className="p-3 rounded"
                      style={{
                        border: namingChoice === 'need_help' ? '2px solid #0d6efd' : '2px solid #dee2e6',
                        backgroundColor: namingChoice === 'need_help' ? '#e7f1ff' : '#fff',
                        cursor: 'pointer'
                      }}
                      onClick={() => { setNamingChoice('need_help'); setProposedName(''); }}
                    >
                      <Form.Check
                        type="radio"
                        id="naming-help"
                        name="namingChoice"
                        label={<span style={{ fontSize: '1.1rem', fontWeight: '500' }}>I need help coming up with a name</span>}
                        checked={namingChoice === 'need_help'}
                        onChange={() => { setNamingChoice('need_help'); setProposedName(''); }}
                      />
                    </div>
                  </div>
                ) : (
                  <Form.Group className="mb-3">
                    <div className="position-relative">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Type your answer here..."
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        ref={answerInputRef}
                        disabled={loading}
                        style={{ paddingRight: '50px' }}
                      />
                      <SpeechInput
                        onStart={() => {
                          // Save current text before speech starts
                          answerBeforeSpeechRef.current = userAnswer;
                        }}
                        onTranscript={(text, isFinal) => {
                          // Show text in real-time (use ref to get current value)
                          const prefix = answerBeforeSpeechRef.current ? answerBeforeSpeechRef.current + ' ' : '';
                          setUserAnswer(prefix + text);
                        }}
                        onError={(msg) => setError(msg)}
                        disabled={loading}
                        className="position-absolute"
                        style={{ right: '8px', bottom: '8px' }}
                        size="sm"
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Be specific - concrete examples help us extract better IP. Click the mic to speak.
                    </Form.Text>
                  </Form.Group>
                )}

                <Button
                  size="lg"
                  className="w-100"
                  onClick={() => {
                    if (questionKey === 's5_naming') {
                      // Pass answer directly to avoid async state issues
                      if (namingChoice === 'have_name' && proposedName.trim()) {
                        handleSubmitAnswer(`NAME: ${proposedName.trim()}`);
                      } else if (namingChoice === 'need_help') {
                        handleSubmitAnswer('NEED_HELP');
                      }
                    } else {
                      handleSubmitAnswer();
                    }
                  }}
                  disabled={loading || (questionKey === 's5_naming'
                    ? (!namingChoice || (namingChoice === 'have_name' && !proposedName.trim()))
                    : !userAnswer.trim())}
                  style={brandButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" />Processing...</>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Naming Phase
  // ============================================
  if (phase === 'naming') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <div className="mb-4">
                  <ProgressBar now={100} style={{ height: '8px', backgroundColor: colors.greyLight }} className="brand-progress" />
                  <p className="mt-2 text-success"><strong>Section 5 of 5: Language Claim</strong></p>
                </div>

                <h3 className="mb-4">Let's Name Your Methodology</h3>

                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

                {/* Synopsis of what was extracted */}
                {synopsis && (
                  <Card className="mb-4" style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4' }}>
                    <Card.Body>
                      <h5 className="mb-3" style={{ color: '#276749' }}>Here's What We Extracted:</h5>

                      {synopsis.expertise && (
                        <div className="mb-3">
                          <strong>Your Expertise:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.95rem' }}>{synopsis.expertise}</p>
                        </div>
                      )}

                      {synopsis.specificExample && (
                        <div className="mb-3">
                          <strong>Specific Example:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.95rem' }}>{synopsis.specificExample.substring(0, 200)}{synopsis.specificExample.length > 200 ? '...' : ''}</p>
                        </div>
                      )}

                      {synopsis.insight && (
                        <div className="mb-3">
                          <strong>Pattern You See:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.95rem' }}>{synopsis.insight}</p>
                        </div>
                      )}

                      {synopsis.domains && synopsis.domains.length > 0 && (
                        <div className="mb-3">
                          <strong>Domains You Synthesize:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.95rem' }}>{Array.isArray(synopsis.domains) ? synopsis.domains.join(', ') : synopsis.domains}</p>
                        </div>
                      )}

                      {synopsis.mistakes && (
                        <div className="mb-3">
                          <strong>Mistakes You Prevent:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.95rem' }}>{synopsis.mistakes.substring(0, 200)}{synopsis.mistakes.length > 200 ? '...' : ''}</p>
                        </div>
                      )}

                      {synopsis.framework && (
                        <div className="mb-3">
                          <strong>Your Decision Framework:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.95rem' }}>{synopsis.framework.substring(0, 200)}{synopsis.framework.length > 200 ? '...' : ''}</p>
                        </div>
                      )}

                      {synopsis.notFor && (
                        <div className="mb-0">
                          <strong>Who It's NOT For:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.95rem' }}>{synopsis.notFor.substring(0, 200)}{synopsis.notFor.length > 200 ? '...' : ''}</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

                {namingOptions ? (
                  <>
                    <p className="text-muted mb-4">
                      Now choose a name for this methodology, or create your own:
                    </p>

                    <h6 className="text-muted mb-2">Descriptive Names (what it does)</h6>
                    <div className="mb-3">
                      {namingOptions.descriptive?.map((name, idx) => (
                        <Form.Check
                          key={`desc-${idx}`}
                          type="radio"
                          id={`name-desc-${idx}`}
                          label={name}
                          name="frameworkName"
                          checked={selectedName === name}
                          onChange={() => setSelectedName(name)}
                          className="mb-2"
                        />
                      ))}
                    </div>

                    <h6 className="text-muted mb-2">Action-Oriented Names (what it achieves)</h6>
                    <div className="mb-3">
                      {namingOptions.action_oriented?.map((name, idx) => (
                        <Form.Check
                          key={`action-${idx}`}
                          type="radio"
                          id={`name-action-${idx}`}
                          label={name}
                          name="frameworkName"
                          checked={selectedName === name}
                          onChange={() => setSelectedName(name)}
                          className="mb-2"
                        />
                      ))}
                    </div>

                    <h6 className="text-muted mb-2">Metaphor-Based Names (conceptual)</h6>
                    <div className="mb-3">
                      {namingOptions.metaphor_based?.map((name, idx) => (
                        <Form.Check
                          key={`meta-${idx}`}
                          type="radio"
                          id={`name-meta-${idx}`}
                          label={name}
                          name="frameworkName"
                          checked={selectedName === name}
                          onChange={() => setSelectedName(name)}
                          className="mb-2"
                        />
                      ))}
                    </div>

                    <hr className="my-4" />

                    <Form.Check
                      type="radio"
                      id="name-custom"
                      label="I have my own name in mind"
                      name="frameworkName"
                      checked={selectedName === 'custom'}
                      onChange={() => setSelectedName('custom')}
                      className="mb-2"
                    />

                    {selectedName === 'custom' && (
                      <Form.Control
                        type="text"
                        placeholder="Enter your framework name..."
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        className="mt-2 mb-3"
                      />
                    )}
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                    <p className="mt-2 text-muted">Generating naming options...</p>
                  </div>
                )}

                <Button
                  size="lg"
                  className="w-100 mt-3"
                  onClick={handleSetName}
                  disabled={loading || (!selectedName || (selectedName === 'custom' && !customName.trim()))}
                  style={successButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                >
                  {loading ? (
                    <><Spinner size="sm" className="me-2" />Finalizing...</>
                  ) : (
                    'Finalize My Framework'
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // ============================================
  // RENDER: Output Phase
  // ============================================
  if (phase === 'output') {
    if (loading || !output) {
      return (
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p>Generating your IP framework...</p>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="text-center mb-4 p-4" style={{ backgroundColor: '#f0fff4', borderRadius: '8px' }}>
              <h2 style={{ color: '#1a365d' }}>Congratulations!</h2>
              <p className="mb-0">You've just <Term term="codified">codified</Term> your <Term term="intellectual property">intellectual property</Term>.</p>
            </div>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            <Card className="shadow mb-4">
              <Card.Header className="bg-primary text-white text-center py-3">
                <h3 className="mb-0">{output.frameworkName}</h3>
              </Card.Header>
              <Card.Body className="p-4">
                <h5 className="text-muted mb-2">What It Is</h5>
                <p className="mb-4">{output.whatItIs}</p>

                <h5 className="text-muted mb-2">Your Core Insight</h5>
                <p className="mb-4">{output.coreInsight}</p>

                <h5 className="text-muted mb-2">Your Decision Framework</h5>
                <p>{output.decisionFramework}</p>
                <p className="mb-4"><strong>Required Sequence:</strong> {output.requiredSequence}</p>

                <h5 className="text-muted mb-2">Domains You Synthesize ({output.domainCount})</h5>
                <div className="mb-4" style={{ columnCount: output.domainCount > 10 ? 2 : 1, columnGap: '2rem' }}>
                  {output.domains && output.domains.split(', ').map((domain, idx) => (
                    <div key={idx} style={{ breakInside: 'avoid', marginBottom: '0.25rem' }}>• {domain}</div>
                  ))}
                </div>

                <h5 className="text-muted mb-2">The Mistake Pattern You Prevent</h5>
                <div className="mb-4" style={{ whiteSpace: 'pre-line' }}>{output.mistakePattern}</div>

                <h5 className="text-muted mb-2">Who It's For</h5>
                <p className="mb-4">{output.whoItsFor}</p>

                <h5 className="text-muted mb-2">Who It's NOT For</h5>
                <div className="mb-4" style={{ whiteSpace: 'pre-line' }}>{output.notFor}</div>

                <h5 className="text-muted mb-2">What You Deliberately Reject</h5>
                <div style={{ whiteSpace: 'pre-line' }}>{output.deliberateRejections}</div>
              </Card.Body>
            </Card>

            {/* Email PDF Section */}
            <Card className="mb-4" style={{ backgroundColor: '#f8fafc' }}>
              <Card.Body className="p-4 text-center">
                <h5 className="mb-3">Get Your Framework as a PDF</h5>
                <p className="text-muted mb-3">
                  We'll email your complete IP framework to preserve your work.
                </p>

                {!pdfSent && !emailError && (
                  <Button
                    size="lg"
                    onClick={() => handleSendPdf(false)}
                    disabled={pdfSending}
                    style={brandButtonStyle}
                    onMouseEnter={(e) => Object.assign(e.target.style, brandButtonHoverStyle)}
                    onMouseLeave={(e) => Object.assign(e.target.style, brandButtonStyle)}
                  >
                    {pdfSending ? (
                      <><Spinner size="sm" className="me-2" />Sending to {formData.email}...</>
                    ) : (
                      <>Email PDF to {formData.email}</>
                    )}
                  </Button>
                )}

                {emailError && !pdfSent && (
                  <div className="mt-3">
                    <Alert variant="warning">
                      {emailError}
                    </Alert>
                    <p className="text-muted mb-2">Update your email address and try again:</p>
                    <div className="d-flex gap-2 justify-content-center">
                      <Form.Control
                        type="email"
                        placeholder="your@email.com"
                        value={updatedEmail}
                        onChange={(e) => setUpdatedEmail(e.target.value)}
                        style={{ maxWidth: '300px' }}
                      />
                      <Button
                        onClick={() => handleSendPdf(true)}
                        disabled={pdfSending || !updatedEmail.trim()}
                        style={{...brandButtonStyle, padding: '0.5rem 1rem', fontSize: '1rem'}}
                        onMouseEnter={(e) => Object.assign(e.target.style, {...brandButtonHoverStyle, padding: '0.5rem 1rem', fontSize: '1rem'})}
                        onMouseLeave={(e) => Object.assign(e.target.style, {...brandButtonStyle, padding: '0.5rem 1rem', fontSize: '1rem'})}
                      >
                        {pdfSending ? <Spinner size="sm" /> : 'Retry'}
                      </Button>
                    </div>
                  </div>
                )}

                {pdfSent && (
                  <Alert variant="success" className="mb-0">
                    PDF sent to {formData.email}! Check your inbox.
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {/* Next Steps */}
            <Card className="shadow">
              <Card.Body className="p-4">
                <h4 className="mb-3">Next Steps: What This <Term term="IP">IP</Term> Becomes</h4>
                <p className="text-muted mb-3">
                  You've just <Term term="codified">codified</Term> ONE piece of <Term term="intellectual property">intellectual property</Term>. This becomes:
                </p>
                <ul>
                  <li>A blog post or article explaining your unique framework</li>
                  <li>A LinkedIn post <Term term="positioning">positioning</Term> you as a thought leader</li>
                  <li>A talk, workshop, or presentation topic</li>
                  <li>Content that AI platforms can <Term term="AI citation">cite and reference</Term></li>
                  <li>A <Term term="methodology">methodology</Term> you can teach, license, or certify</li>
                  <li><Term term="schema">Schema</Term>-marked content that builds your <Term term="knowledge graph">knowledge graph</Term> presence</li>
                  <li>Authority that compounds over time</li>
                </ul>

                <hr />

                <h5 className="mb-3" style={{ color: '#276749' }}>Why Documented <Term term="IP">IP</Term> Changes Everything</h5>
                <p className="mb-3">
                  When your <Term term="intellectual property">intellectual property</Term> is clearly documented and published, something powerful happens:
                </p>
                <ul className="mb-3">
                  <li><strong>AI platforms start <Term term="AI citation">citing you</Term></strong> — your expertise shows up in ChatGPT, Claude, and Perplexity answers</li>
                  <li><strong>Trust compounds</strong> — prospects see your <Term term="methodology">methodology</Term> before they ever contact you</li>
                  <li><strong>Premium <Term term="positioning">positioning</Term></strong> — you're no longer competing on price, you're the recognized expert</li>
                  <li><strong>Business valuation increases</strong> — documented <Term term="IP">IP</Term> is one of the first things buyers and investors look for</li>
                </ul>

                <hr />

                <h5 className="mb-3">Want to Refine This Framework?</h5>
                <p className="text-muted mb-3">
                  This is a first draft of your <Term term="IP">IP</Term>. Inside the <strong>Command Center</strong>, you can:
                </p>
                <ul className="mb-3">
                  <li><strong>Edit and refine</strong> each section until it's perfect</li>
                  <li><strong>Add more frameworks</strong> — most experts have 5-10 pieces of valuable <Term term="IP">IP</Term></li>
                  <li><strong>Track your progress</strong> as AI platforms start recognizing you</li>
                  <li><strong>Turn <Term term="IP">IP</Term> into content</strong> — blog posts, social content, sales pages</li>
                </ul>

                <hr />

                <h5 className="mb-3">You Have More <Term term="IP">IP</Term> Than You Realize</h5>
                <p className="text-muted mb-3">
                  Most experts have <strong>5-10 pieces</strong> of valuable, <Term term="codified">codifiable</Term> <Term term="intellectual property">intellectual property</Term>{' '}
                  they've never extracted or documented. Frameworks, decision processes, unique perspectives —
                  it's all there, just waiting to be articulated.
                </p>
                <p className="mb-4">
                  Inside the <strong>Command Center</strong>, we help you extract ALL your valuable <Term term="IP">IP</Term>{' '}
                  and then do something most experts never figure out: <em>we help you communicate it</em>.
                  Blog posts, social content, email sequences, sales pages — we take the pain, frustration,
                  and tedium out of the process so you can focus on what you do best.
                </p>
                <Button
                  size="lg"
                  href="https://www.marketdomination.solutions/ai-profit-engineer/"
                  target="_blank"
                  className="w-100"
                  style={successButtonStyle}
                  onMouseEnter={(e) => Object.assign(e.target.style, successButtonHoverStyle)}
                  onMouseLeave={(e) => Object.assign(e.target.style, successButtonStyle)}
                >
                  Explore The Command Center
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default MindToMethod;
